import React from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";

import howItWorksBg from "../../../assets/Images/HowItWorks.svg";
import howItWorksTop from "../../../assets/Images/HowItWorksTop.png";
import stringHand from "../../../assets/Elements/string-hand.svg";

const defaultSteps = [
  {
    id: 1,
    title: "We Feature Vendors",
    description:
      "Our team identifies and verifies hardworking small street vendors and features their story and products on our platform.",
  },
  {
    id: 2,
    title: "You Buy Their Products",
    description:
      "Browse vendor profiles, watch their story video, and purchase their products directly through our secure payment gateway.",
  },
  {
    id: 3,
    title: "Products Go to NGOs",
    description:
      "The products you buy are distributed to our partner NGOs - orphanages, old age homes, shelters, and community kitchens.",
  },
  {
    id: 4,
    title: "Double The Impact",
    description:
      "The vendor earns a fair income and the NGO receives essential products. Both benefit from a single act of kindness.",
  },
];

const StepCard = ({ step, compact = false, mobile = false }) => (
  <Box
    sx={{
      background: "#FFFFFF",
      borderRadius: mobile ? "14px" : "20px",
      p: mobile ? "16px 14px" : compact ? "20px" : "30px",
      minHeight: compact ? "auto" : "262px",
      boxSizing: "border-box",
    }}
  >
    <Box
      sx={{
        width: mobile ? "36px" : "50px",
        height: mobile ? "36px" : "50px",
        borderRadius: mobile ? "7.2px" : "12.5px",
        background: "rgba(74, 74, 73, 0.1)",
        display: "grid",
        placeItems: "center",
        mb: "14px",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: mobile ? "14px" : compact ? "18px" : "21px", lineHeight: mobile ? "16.747px" : compact ? "20px" : "23px", color: "#4A4A49" }}>
        {step.id}
      </Typography>
    </Box>
    <Typography sx={{ fontWeight: 700, fontSize: mobile ? "14px" : compact ? "16px" : "18px", lineHeight: "normal", letterSpacing: mobile ? "-0.14px" : "-0.18px", color: "#151A33", mb: mobile ? "8px" : "14px" }}>
      {step.title}
    </Typography>
    <Typography sx={{ fontWeight: 400, fontSize: mobile ? "12px" : "14px", lineHeight: mobile ? "19.416px" : compact ? "22px" : "26px", opacity: 0.7, color: "#1D1D1B" }}>
      {step.description}
    </Typography>
  </Box>
);

const HowItWorksSection = () => {
  const theme = useTheme();
  const home = useSelector((state) => state.cms.home);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery("(min-width:600px) and (max-width:899.95px)");
  const isLaptop = useMediaQuery("(min-width:900px) and (max-width:1199.95px)");
  const isDesktop = useMediaQuery("(min-width:1200px)");

  const sections = Array.isArray(home?.data) ? home.data : [];
  const howItWorksCms = sections.find((item) => {
    if (!item) return false;
    const isActive = item.status === 1 || item.status === true;
    if (!isActive) return false;
    return String(item.title || "").toLowerCase() === "how it works";
  });

  const sectionHeading = howItWorksCms?.sub_title || "How Can Buying From Small Vendors Help?";
  const sectionDescription =
    howItWorksCms?.description ||
    "A simple purchase creates a chain of goodness - supporting the vendor's livelihood while donating products to NGOs serving those in need.";
  const overlayImage = howItWorksCms?.image || howItWorksTop;

  const cmsSteps = Array.isArray(howItWorksCms?.relatedCMS)
    ? howItWorksCms.relatedCMS
        .filter((item) => item && (item.status === 1 || item.status === true))
        .map((item, index) => ({
          id: index + 1,
          title: item?.title || "",
          description: item?.description || "",
        }))
    : [];

  const steps = cmsSteps.length > 0 ? cmsSteps : defaultSteps;

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "94.44%",
        maxWidth: "1440px",
        mx: "auto",
        px: isMobile ? "16px" : 0,
        mb: isMobile ? "30px" : "60px",
      }}
    >
      <Box
        sx={{
          background: "#F7F7F7",
          borderRadius: isMobile ? "16px" : "20px",
          overflow: "hidden",
          position: "relative",
          p: isMobile ? "20px 16px" : isTablet ? "28px 22px 22px" : isLaptop ? "34px 24px 24px" : "40px 30px 30px",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: isMobile ? "16px" : isTablet ? "30px" : "86px",
            top: isMobile ? "18px" : isTablet ? "30px" : "60px",
            opacity: isMobile ? 0.3 : 1,
            display: isMobile ? "none" : "block",
          }}
        >
          <Box
            component="img"
            src={stringHand}
            alt=""
            sx={{ width: isMobile ? "70px" : isTablet ? "110px" : "180px", height: "auto", objectFit: "contain", transform: "scaleX(-1)" }}
          />
        </Box>

        <Box sx={{ mb: isMobile ? "20px" : "24px", position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "13px" : "12px", mb: "12px" }}>
            <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", background: "#A9C74E" }} />
            <Typography sx={{ fontWeight: 400, fontSize: isMobile ? "10px" : "14px", letterSpacing: isMobile ? "2px" : "2.1px", textTransform: "uppercase", color: "#131313", lineHeight: isMobile ? 1 : "18px" }}>
              How It Works
            </Typography>
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "18px" : isTablet ? "32px" : "36px", lineHeight: isMobile ? "29.124px" : "1.4", letterSpacing: "-0.36px", color: "#1D1D1B", mb: "10px", maxWidth: isMobile ? "243px" : isLaptop ? "900px" : "none" }}>
            {sectionHeading}
            {isMobile ? (
              <Box component="span" sx={{ color: "#F04951", ml: "6px", fontSize: "18px", lineHeight: 1 }}>
                ♥
              </Box>
            ) : null}
          </Typography>
          <Typography sx={{ fontWeight: 400, fontSize: isMobile ? "12px" : "16px", lineHeight: isMobile ? "20px" : "24px", letterSpacing: isMobile ? "-0.24px" : "normal", color: "#7D7D7D", maxWidth: isMobile ? "328px" : "1161px" }}>
            {sectionDescription}
          </Typography>
        </Box>

        {isDesktop ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "minmax(320px,1fr) minmax(300px,395px) minmax(320px,1fr)", gap: "24px", alignItems: "center", justifyContent: "flex-start", position: "relative", zIndex: 1 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <StepCard step={steps[0]} />
              <StepCard step={steps[1]} />
            </Box>

            <Box
              sx={{
                width: "395px",
                height: "540px",
                borderRadius: "20px",
                backgroundColor: "#A9C74E",
                backgroundImage: `url(${howItWorksBg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={overlayImage}
                alt="How it works visual"
                sx={{
                  width: "395px",
                  height: "446px",
                  objectFit: "cover",
                  objectPosition: "center bottom",
                }}
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <StepCard step={steps[2]} />
              <StepCard step={steps[3]} />
            </Box>
          </Box>
        ) : isLaptop ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "16px",
              position: "relative",
              zIndex: 1,
            }}
          >
            <StepCard step={steps[0]} compact />
            <StepCard step={steps[1]} compact />
            <Box
              sx={{
                gridColumn: "1 / -1",
                width: "100%",
                borderRadius: "20px",
                backgroundColor: "#A9C74E",
                backgroundImage: `url(${howItWorksBg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                overflow: "hidden",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                minHeight: "430px",
              }}
            >
              <Box component="img" src={overlayImage} alt="How it works visual" sx={{ width: "100%", maxWidth: "360px", height: "auto", objectFit: "contain" }} />
            </Box>
            <StepCard step={steps[2]} compact />
            <StepCard step={steps[3]} compact />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "14px" : "16px", position: "relative", zIndex: 1 }}>
            <StepCard step={steps[0]} compact mobile={isMobile} />
            <StepCard step={steps[1]} compact mobile={isMobile} />
            {!isMobile && (
              <Box
                sx={{
                  width: "100%",
                  borderRadius: "20px",
                  backgroundColor: "#A9C74E",
                  backgroundImage: `url(${howItWorksBg})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  minHeight: isTablet ? "430px" : "360px",
                }}
              >
                <Box
                  component="img"
                  src={overlayImage}
                  alt="How it works visual"
                  sx={{
                    width: "100%",
                    maxWidth: isTablet ? "380px" : "320px",
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}
            <StepCard step={steps[2]} compact mobile={isMobile} />
            <StepCard step={steps[3]} compact mobile={isMobile} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default HowItWorksSection;
